import '@/App.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CanonicalTag from '@/components/CanonicalTag';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PwaInstallProvider } from '@/lib/pwaInstall';
import { ArenaxCapabilityProvider } from '@/lib/arenaxCapabilities';
// Phase 178.6.E — Global analytics sink + capability-aware enrichment.
import { AnalyticsProvider } from '@/providers/AnalyticsProvider';
import api from '@/lib/api';
import { recordPageView } from '@/lib/telemetry';
// Phase 277 — Public website tracking telemetry (Super Admin web analytics).
import useWebTelemetry from '@/hooks/useWebTelemetry';
import { Toaster } from '@/components/ui/sonner';
import Landing from '@/pages/Landing';
// Phase 283 — Public Book-a-Call route (CTA target from Pitch Email)
import BookCall from '@/pages/BookCall';
import WeeklyGoalCapture from '@/pages/WeeklyGoalCapture';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
// Phase 322a — Self-signup funnel (Razorpay Payment Link → zero-touch tenant provisioning → magic link handoff)
import GetStarted from '@/pages/GetStarted';
import OnboardingHandoff from '@/pages/OnboardingHandoff';
// Phase 322b — 4-step Academy Onboarding Wizard
import AcademyWizard from '@/pages/AcademyWizard';
// Phase 605 — "AI Sales Employee" deprecated. Replaced by the 4-team
//   AI Agency Command Center at /super-admin/agency. Old URL kept as a
//   client-side redirect below so bookmarked links don't 404.
import GrowthCommandCenter from '@/pages/super-admin/GrowthCommandCenter';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Students from '@/pages/Students';
import HRMS from '@/pages/HRMS';
import Events from '@/pages/Events';
import Finance from '@/pages/Finance';
import FinanceAutopilot from '@/pages/FinanceAutopilot';
import SuperAdmin from '@/pages/SuperAdmin';
import SettingsPage from '@/pages/Settings';
import StudentPortal from '@/pages/StudentPortal';
import StudentEvents from '@/pages/StudentEvents';
import StudentChat from '@/pages/StudentChat';
import EmployeeChat from '@/pages/EmployeeChat';
import CoachAnalytics from '@/pages/CoachAnalytics';
import StudentTraining from '@/pages/StudentTraining';
import MatchAnalysis from '@/pages/MatchAnalysis';
import EmployeePortal from '@/pages/EmployeePortal';
import CRM from '@/pages/CRM';
import Communications from '@/pages/Communications';
import HelpCenter from '@/pages/HelpCenter';
import NotificationsInbox from '@/pages/NotificationsInbox';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import VerifyAcademy from '@/pages/VerifyAcademy';
import VerifyRequest from '@/pages/VerifyRequest';
import PublicEnroll from '@/pages/PublicEnroll';
import CoachDashboard from '@/pages/CoachDashboard';
import CoachQrScannerScreen from '@/pages/CoachQrScannerScreen';
import ParentScanReceiver from '@/pages/ParentScanReceiver';
import FeedbackRouter from '@/pages/FeedbackRouter';
import RecoveryInbox from '@/pages/RecoveryInbox';
import KioskMode from '@/pages/KioskMode';
import KioskOnboardLanding from '@/pages/KioskOnboardLanding';
import ReferralJoinLanding from '@/pages/ReferralJoinLanding';
// Phase 308 — Staff Timesheets owner dashboard
import StaffTimesheets from '@/pages/StaffTimesheets';
// Phase 312 — Coach Performance Scorecards owner dashboard
import CoachScorecards from '@/pages/CoachScorecards';
import KPIDashboard from '@/pages/KPIDashboard';
import Subscription from '@/pages/Subscription';
import AcademyCRM from '@/pages/AcademyCRM';
import InboundCRM from '@/pages/InboundCRM';
import AdminHRMS from '@/pages/admin/AdminHRMS';
import AdminFinance from '@/pages/admin/AdminFinance';
import AdminAcademies from '@/pages/admin/AdminAcademies';
import AdminPersonProfile from '@/pages/admin/AdminPersonProfile';
import AdminTrials from '@/pages/admin/AdminTrials';
import WallOfChampions from '@/pages/WallOfChampions';
import WallOfChampionsGateway from '@/pages/WallOfChampionsGateway';
import HonorsManager from '@/pages/admin/HonorsManager';
import EventCheckIn from '@/pages/EventCheckIn';
import EventGatePass from '@/pages/EventGatePass';
import SocialBee from '@/pages/SocialBee';
import SuperAdminTiers from '@/pages/SuperAdminTiers';
// Phase 266c — A/B funnel dashboard (Meta vs Twilio dispatch report)
import SuperAdminCommsAB from '@/pages/SuperAdminCommsAB';
import AdminInvoices from '@/pages/admin/AdminInvoices';
import AdminAnnounce from '@/pages/admin/AdminAnnounce';
import DigestPreferences from '@/pages/admin/DigestPreferences';
import AutonomousLedger from '@/pages/admin/AutonomousLedger';
import BillingHealthDashboard from '@/pages/admin/BillingHealthDashboard';
import AdminAdoption from '@/pages/admin/AdminAdoption';
import PublicPay from '@/pages/PublicPay';
import PublicPayStudentFee from '@/pages/PublicPayStudentFee';
import PublicAttendanceScan from '@/pages/PublicAttendanceScan';
// Phase 138H.b — Academy owner fee-dunning control center
import DunningControlPanel from '@/pages/DunningControlPanel';
// Phase 195.e — Super Admin Onboarding Media & CRM Blast Engine
import SuperAdminWalkthroughBlast from '@/pages/SuperAdminWalkthroughBlast';
// Phase 267 — Super Admin CRM Directory + per-row Outreach Console
import SuperAdminCrmDirectory from '@/pages/SuperAdminCrmDirectory';
import SuperAdminWebhooks from '@/pages/SuperAdminWebhooks';
// Phase 277 — Super Admin Website Tracking
import SuperAdminWebAnalytics from '@/pages/SuperAdminWebAnalytics';
// Phase 220 — Super Admin in-app walkthrough tutorial library
import SuperAdminWalkthroughs from '@/pages/SuperAdminWalkthroughs';
import AdminPortfolio from '@/pages/admin/AdminPortfolio';
import VideoAnalysis from '@/pages/admin/VideoAnalysis';
import Nutrition from '@/pages/Nutrition';
import Features from '@/pages/Features';
// Phase 130 — StreamX teaser (broadcasting product, marketing-only for now)
import StreamX from '@/pages/LiveX';
// Phase 130B — StreamX waitlist demand dashboard (super_admin only)
import LivexWaitlistDashboard from '@/pages/LivexWaitlistDashboard';
// Phase 131 — StreamX Alpha (live broadcasting prototype)
import LivexStudio from '@/pages/livex/LivexStudio';
import LivexWatch from '@/pages/livex/LivexWatch';
import LivexHistory from '@/pages/livex/LivexHistory';
import LivexAcademyTopMoments from '@/pages/livex/LivexAcademyTopMoments';
import LivexLive from '@/pages/livex/LivexLive';
// Phase 132 — StreamX SaaS layer
import LivexPricing from '@/pages/livex/LivexPricing';
import LivexSubscription from '@/pages/livex/LivexSubscription';
import LivexDashboard from '@/pages/livex/LivexDashboard';
import LivexAdminOverview from '@/pages/livex/admin/LivexAdminOverview';
import LivexAdminAcademies from '@/pages/livex/admin/LivexAdminAcademies';
import LivexAdminInvoices from '@/pages/livex/admin/LivexAdminInvoices';
import LivexAdminEmails from '@/pages/livex/admin/LivexAdminEmails';
import LivexAdminCohorts from '@/pages/livex/admin/LivexAdminCohorts';
// Phase 128 — Super Admin WhatsApp Templates Console
import AdminWhatsAppConsole from '@/pages/admin/AdminWhatsAppConsole';
import LivexReplays from '@/pages/livex/LivexReplays';
import LivexReplayDetail from '@/pages/livex/LivexReplayDetail';
import LivexPublicReplay from '@/pages/livex/LivexPublicReplay';
// Phase 140B.2 Sprint 4 — StreamX Highlight Library (owner-facing clip gallery)
import LivexHighlights from '@/pages/livex/LivexHighlights';
// Phase 135D — StreamX Stories (Weekly Editorial Layer)
import LivexStories from '@/pages/livex/LivexStories';
import FAQ from '@/pages/FAQ';
import PaymentAnalytics from '@/pages/PaymentAnalytics';
import ParentPortal from '@/pages/ParentPortal';
import TrainingSessions from '@/pages/TrainingSessions';
import DrillLibrary from '@/pages/DrillLibrary';
// Phase 641 — Session Plan Builder (drag-drop drill composer)
import SessionPlansListPage from '@/pages/SessionPlansListPage';
import SessionPlanBuilderPage from '@/pages/SessionPlanBuilderPage';
// Phase 640 — Video Analysis MVP (coach ↔ parent feedback loop)
import VideoAnalysisListPage from '@/pages/VideoAnalysisListPage';
import VideoAnalysisStudioPage from '@/pages/VideoAnalysisStudioPage';
import PublicVideoAnalysisPage from '@/pages/PublicVideoAnalysisPage';
import InjuryTracker from '@/pages/InjuryTracker';
import LeadIntelligence from '@/pages/LeadIntelligence';
import LeadIntelDashboard from '@/pages/LeadIntelDashboard';
import DigitalLocker from '@/pages/DigitalLocker';
import FunnelAnalytics from '@/pages/FunnelAnalytics';
import SuperAdminAcademyCRM from '@/pages/SuperAdminAcademyCRM';
import SuperAdminAgencyCRM from '@/pages/super-admin/SuperAdminAgencyCRM';
import Unsubscribe from '@/pages/Unsubscribe';
import PublicScoutProfile from '@/pages/PublicScoutProfile';
import MagicLogin from '@/pages/MagicLogin';
import MessageLogs from '@/pages/MessageLogs';
import Profile from '@/pages/Profile';
import AppShell from '@/components/layout/AppShell';
import FirstLoginResetModal from '@/components/auth/FirstLoginResetModal';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';

// Phase 200 — ArenaX (AI-Native Pay & Play Ecosystem)
import ArenaxLanding from '@/pages/arenax/ArenaxLanding';
import ArenaxVenues from '@/pages/arenax/ArenaxVenues';
import ArenaxVenueDetail from '@/pages/arenax/ArenaxVenueDetail';
import ArenaxLogin from '@/pages/arenax/ArenaxLogin';
import ArenaxRegister from '@/pages/arenax/ArenaxRegister';
import ArenaxVerify from '@/pages/arenax/ArenaxVerify';
import ArenaxAuthCallback from '@/pages/arenax/ArenaxAuthCallback';
import ArenaxPlayerDashboard from '@/pages/arenax/ArenaxPlayerDashboard';
import ArenaxPlayerOnboarding from '@/pages/arenax/ArenaxPlayerOnboarding';
import ArenaxPlayerBookings from '@/pages/arenax/ArenaxPlayerBookings';
import ArenaxVenueAdmin from '@/pages/arenax/ArenaxVenueAdmin';
import ArenaxSuperAdmin from '@/pages/arenax/ArenaxSuperAdmin';
import ArenaxOps from '@/pages/arenax/ArenaxOps';
import ArenaxMythologyOps from '@/pages/arenax/ArenaxMythologyOps';
import ArenaxOperatorHost from '@/pages/arenax/ArenaxOperatorHost';
import ArenaxHostAcademy from '@/pages/arenax/ArenaxHostAcademy';
import ArenaxHostVenue from '@/pages/arenax/ArenaxHostVenue';
import ArenaxHostOrganizer from '@/pages/arenax/ArenaxHostOrganizer';
import ArenaxHostAcademyManage from '@/pages/arenax/ArenaxHostAcademyManage';
import ArenaxHostVenueManage from '@/pages/arenax/ArenaxHostVenueManage';
import ArenaxHostOrganizerManage from '@/pages/arenax/ArenaxHostOrganizerManage';
// Phase 178.5.F — Dev sandbox for BulletproofContextMenu primitive
import ContextMenuSandbox from '@/pages/_sandbox/ContextMenuSandbox';
import ArenaxVenuePublic from '@/pages/arenax/ArenaxVenuePublic';
import ArenaxMyBookings from '@/pages/arenax/ArenaxMyBookings';
// Phase 2A — Public Games Engine
import ArenaxGames from '@/pages/arenax/ArenaxGames';
import ArenaxGameDetail from '@/pages/arenax/ArenaxGameDetail';
import ArenaxHostGame from '@/pages/arenax/ArenaxHostGame';
// Phase 2B — Squads (retention layer)
import ArenaxSquads from '@/pages/arenax/ArenaxSquads';
import ArenaxSquadDetail from '@/pages/arenax/ArenaxSquadDetail';
import ArenaxTournaments from '@/pages/arenax/ArenaxTournaments';
import ArenaxTournamentDetail from '@/pages/arenax/ArenaxTournamentDetail';
import ArenaxTournamentCreate from '@/pages/arenax/ArenaxTournamentCreate';
import ArenaxSeasonDetail from '@/pages/arenax/ArenaxSeasonDetail';
import ArenaxSeasons from '@/pages/arenax/ArenaxSeasons';
import ArenaxRankings from '@/pages/arenax/ArenaxRankings';
import ArenaxCityFeed from '@/pages/arenax/ArenaxCityFeed';
import ArenaxStories from '@/pages/arenax/ArenaxStories';
import ArenaxSpectatorRoom from '@/pages/arenax/ArenaxSpectatorRoom';
import ArenaxRivalryDocumentary from '@/pages/arenax/ArenaxRivalryDocumentary';
import ArenaxCreateSquad from '@/pages/arenax/ArenaxCreateSquad';
import ArenaxJoinSquad from '@/pages/arenax/ArenaxJoinSquad';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Auto-extend session is active behind every protected route
  useSessionHeartbeat();

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

// Phase 90.3 — Role-scoped route gate. Wraps ProtectedRoute and additionally
// asserts the user.role is in the allowed list. Unauthorized roles are
// redirected to their own role-home (NOT to /login — they're authenticated,
// just not authorized for this URL).
function RoleGate({ allow, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) {
    // Send them to their proper home instead of leaking the wrong page.
    const home =
      user.role === 'super_admin' ? '/super-admin' :
      user.role === 'student' ? '/student' :
      user.role === 'parent' ? '/parent' :
      user.role === 'coach' ? '/coach' :
      user.role === 'employee' ? '/employee' :
      user.role === 'player' ? '/arenax/player' :
      '/dashboard';
    return <Navigate to={home} replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  if (user.role === 'student') return <Navigate to="/student" replace />;
  if (user.role === 'parent') return <Navigate to="/parent" replace />;
  if (user.role === 'coach') return <Navigate to="/coach" replace />;
  if (user.role === 'employee') return <Navigate to="/employee" replace />;
  if (user.role === 'player') return <Navigate to="/arenax/player" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Phase 99 — Universal /dashboard entry point. Renders the Academy Owner
// Dashboard inside the shell for owners; everyone else is silently routed to
// the page that IS their dashboard (CoachDashboard, StudentPortal, etc.).
// This means EVERY role can safely link to "/dashboard" without 403s, and the
// Login redirect can target /dashboard universally.
function UnifiedDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  if (user.role === 'student') return <Navigate to="/student" replace />;
  if (user.role === 'parent') return <Navigate to="/parent" replace />;
  if (user.role === 'coach' || user.role === 'employee') return <Navigate to="/coach" replace />;
  if (user.role === 'player') return <Navigate to="/arenax/player" replace />;
  // Owner — render the actual Dashboard inside the shell.
  return <AppShell><Dashboard /></AppShell>;
}

// Phase 182-R — Telemetry watcher.  Records a single anonymous pageview ping
// per public ecosystem route change.  Mounted once near the top of the router.
// Phase 277 — Also drives the marketing-site web-analytics hook so Super Admins
// can see Pricing-page drop-offs in /super-admin/web-analytics.
function TelemetryWatcher() {
  const location = useLocation();
  const lastPath = useRef('');
  useWebTelemetry(); // Phase 277 — public-site telemetry beacon
  useEffect(() => {
    const p = location.pathname || '';
    if (!p || p === lastPath.current) return;
    // Phase 279 — Google Analytics 4 SPA pageview wiring.  We disabled
    // gtag's auto page_view in index.html (send_page_view:false) so
    // every client-side navigation must fire one explicit page_view
    // event here.  Skip authenticated app routes entirely so internal
    // staff browsing doesn't pollute the marketing GA property.
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: p + (location.search || ''),
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (p === '/' || p.startsWith('/streamx') || p.startsWith('/livex') || p.startsWith('/arenax')) {
      lastPath.current = p;
      recordPageView(p);
    }
  }, [location.pathname, location.search]);
  return null;
}


function App() {
  return (
    <AuthProvider>      <PwaInstallProvider>
      <ArenaxCapabilityProvider>
      <AnalyticsProvider>
      <BrowserRouter>
        <CanonicalTag />
        <TelemetryWatcher />
        {/* Phase 326e — Forced password-reset modal. Renders at the root so
            it covers every authenticated page. Returns null when not needed. */}
        <FirstLoginResetModal />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/book-call" element={<BookCall />} />
          <Route path="/goals/:token" element={<WeeklyGoalCapture />} />
          <Route path="/login" element={<Login />} />
          <Route path="/magic-login" element={<MagicLogin />} />
          <Route path="/signup" element={<Signup />} />
          {/* Phase 322a — Public self-signup funnel */}
          <Route path="/get-started" element={<GetStarted />} />
          {/* Phase 322a — Magic-link landing after Razorpay payment_captured webhook */}
          <Route path="/onboarding" element={<OnboardingHandoff />} />
          {/* Phase 322b — 4-step onboarding wizard (academy_owner only) */}
          <Route path="/onboarding/wizard" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><AcademyWizard /></RoleGate></ProtectedRoute>} />

          {/* Phase 605 — /super-admin/sales-ai deprecated. Redirects to the
              new AI Agency Command Center so bookmarks/deep-links don't 404. */}
          <Route path="/super-admin/sales-ai" element={<Navigate to="/super-admin/agency" replace />} />
          {/* Phase 550c — AI Agency Command Center (live 4-team telemetry; Slice 1 ships Teams 1+2) */}
          <Route path="/super-admin/agency" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><GrowthCommandCenter /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 630 — Super Admin Agency CRM (Team 1 B2B lead management) */}
          <Route path="/super-admin/agency-crm" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><SuperAdminAgencyCRM /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 605 — Auto-redirect retired URLs straight to the AI Agency Command Center (single hop). */}
          <Route path="/super-admin/leads"          element={<Navigate to="/super-admin/agency-crm" replace />} />
          <Route path="/super-admin/funnel"         element={<Navigate to="/super-admin/agency" replace />} />
          <Route path="/super-admin/crm"            element={<Navigate to="/super-admin/agency-crm" replace />} />
          <Route path="/super-admin/crm-directory"  element={<Navigate to="/super-admin/agency" replace />} />
          <Route path="/super-admin/web-analytics"  element={<Navigate to="/super-admin/agency" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pay/student-fee/:feeId" element={<PublicPayStudentFee />} />
          <Route path="/pay/:invoiceId" element={<PublicPay />} />
          <Route path="/attend/:token" element={<PublicAttendanceScan />} />
          {/* Phase 297 — Parent NPS / Google Review receiver. PUBLIC route (no auth) — the signed JWT in :token is the sole credential. */}
          <Route path="/feedback/:token" element={<FeedbackRouter />} />
          {/* Phase 302 — Kiosk onboard landing. PUBLIC. Signed kiosk_token in ?token=<JWT> is the sole credential. */}
          <Route path="/onboard" element={<KioskOnboardLanding />} />
          {/* Phase 306 — PLG Referral Join Landing (PUBLIC). Closes the
              viral loop opened by Phase 305's QR-on-PDF receipts. */}
          <Route path="/join/:academy_id" element={<ReferralJoinLanding />} />
          <Route path="/features" element={<Features />} />
          <Route path="/livex" element={<StreamX />} />
          {/* Phase 132 — StreamX SaaS surface */}
          <Route path="/livex/pricing" element={<LivexPricing />} />
          <Route path="/livex/dashboard" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><LivexDashboard /></RoleGate></ProtectedRoute>} />
          <Route path="/livex/subscription" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><LivexSubscription /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/livex" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminOverview /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/livex/academies" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminAcademies /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/livex/invoices" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminInvoices /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/livex/emails" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminEmails /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/livex/cohorts" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminCohorts /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/whatsapp" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AdminWhatsAppConsole /></RoleGate></ProtectedRoute>} />
          <Route path="/livex/replays" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexReplays /></RoleGate></ProtectedRoute>} />
          <Route path="/livex/replays/public/:slug" element={<LivexPublicReplay />} />
          <Route path="/livex/stories" element={<LivexStories />} />
          <Route path="/livex/replays/:replayId" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexReplayDetail /></RoleGate></ProtectedRoute>} />
          {/* Phase 131E — Public live-stream discovery */}
          <Route path="/livex/live" element={<LivexLive />} />
          {/* Phase 131 — StreamX Alpha */}
          <Route path="/livex/watch/:streamId" element={<LivexWatch />} />
          <Route path="/livex/studio" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexStudio /></RoleGate></ProtectedRoute>} />
          <Route path="/livex/studio/:streamId" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexStudio /></RoleGate></ProtectedRoute>} />
          <Route path="/livex/history" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexHistory /></RoleGate></ProtectedRoute>} />
          {/* Phase 140B.2 Sprint 4 — Highlight Library (owner-facing clip gallery) */}
          <Route path="/livex/highlights" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexHighlights /></RoleGate></ProtectedRoute>} />
          {/* Phase 131D — Academy Top Moments (PUBLIC) */}
          <Route path="/livex/academy/:slug/top-moments" element={<LivexAcademyTopMoments />} />
          <Route path="/super-admin/livex-waitlist"
            element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexWaitlistDashboard /></RoleGate></ProtectedRoute>} />

          {/* ─────────────────────────────────────────────────────────
              StreamX Rebrand Sprint 1 — Dual-mounted canonical routes.
              Every /livex/* path above gets a /streamx/* mirror.  Old
              URLs continue to work forever (no redirect) so replay
              links, WhatsApp shares, indexed pages, and academy
              bookmarks survive the rebrand intact.
              ───────────────────────────────────────────────────────── */}
          <Route path="/streamx" element={<StreamX />} />
          <Route path="/streamx/pricing" element={<LivexPricing />} />
          <Route path="/streamx/dashboard" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><LivexDashboard /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/subscription" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><LivexSubscription /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/streamx" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminOverview /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/streamx/academies" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminAcademies /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/streamx/invoices" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminInvoices /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/streamx/emails" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminEmails /></RoleGate></ProtectedRoute>} />
          <Route path="/admin/streamx/cohorts" element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexAdminCohorts /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/replays" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexReplays /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/replays/public/:slug" element={<LivexPublicReplay />} />
          <Route path="/streamx/stories" element={<LivexStories />} />
          <Route path="/streamx/replays/:replayId" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexReplayDetail /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/live" element={<LivexLive />} />
          <Route path="/streamx/watch/:streamId" element={<LivexWatch />} />
          <Route path="/streamx/studio" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexStudio /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/studio/:streamId" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexStudio /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/history" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexHistory /></RoleGate></ProtectedRoute>} />
          {/* Phase 140B.2 Sprint 4 — Highlight Library mirror */}
          <Route path="/streamx/highlights" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><LivexHighlights /></RoleGate></ProtectedRoute>} />
          <Route path="/streamx/academy/:slug/top-moments" element={<LivexAcademyTopMoments />} />
          <Route path="/super-admin/streamx-waitlist"
            element={<ProtectedRoute><RoleGate allow={['super_admin']}><LivexWaitlistDashboard /></RoleGate></ProtectedRoute>} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/scout/:slug/:jersey" element={<PublicScoutProfile />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          {/* Phase 85 — public Wall of Champions (no auth) */}
          <Route path="/academy/:slug" element={<WallOfChampions />} />
          <Route path="/wall-of-champions" element={<ProtectedRoute><AppShell><WallOfChampionsGateway /></AppShell></ProtectedRoute>} />
          {/* Phase 138I — Structured Honors Logging (academy_owner CRUD) */}
          <Route path="/honors-manager" element={<ProtectedRoute><AppShell><HonorsManager /></AppShell></ProtectedRoute>} />
          {/* Phase 87 — Printable Gate Pass (auth required, no AppShell for clean print layout) */}
          <Route path="/events/:eventId/registrations/:regId/gatepass" element={<ProtectedRoute><EventGatePass /></ProtectedRoute>} />
          <Route path="/verify" element={<VerifyAcademy />} />
          <Route path="/verify-request" element={<VerifyRequest />} />
          <Route path="/enroll/:event_id" element={<PublicEnroll />} />
          
          {/* Phase 99 — Unified /dashboard entry-point. Role-redirects so every
              role's "Dashboard" sidebar item lands on the correct module. */}
          <Route path="/dashboard" element={<ProtectedRoute><UnifiedDashboard /></ProtectedRoute>} />
          
          <Route path="/student" element={<ProtectedRoute><AppShell><StudentPortal /></AppShell></ProtectedRoute>} />
          <Route path="/student/profile" element={<ProtectedRoute><AppShell><StudentPortal /></AppShell></ProtectedRoute>} />
          <Route path="/student/locker" element={<ProtectedRoute><AppShell><DigitalLocker /></AppShell></ProtectedRoute>} />
          <Route path="/student/events" element={<ProtectedRoute><AppShell><StudentEvents /></AppShell></ProtectedRoute>} />
          <Route path="/student-events" element={<ProtectedRoute><AppShell><StudentEvents /></AppShell></ProtectedRoute>} />
          <Route path="/student/chat" element={<ProtectedRoute><AppShell><StudentChat /></AppShell></ProtectedRoute>} />
          <Route path="/student-chat" element={<ProtectedRoute><AppShell><StudentChat /></AppShell></ProtectedRoute>} />
          <Route path="/student/training" element={<ProtectedRoute><AppShell><StudentTraining /></AppShell></ProtectedRoute>} />
          <Route path="/student-training" element={<ProtectedRoute><AppShell><StudentTraining /></AppShell></ProtectedRoute>} />
          <Route path="/student/match-analysis" element={<ProtectedRoute><AppShell><MatchAnalysis /></AppShell></ProtectedRoute>} />
          <Route path="/match-analysis" element={<ProtectedRoute><AppShell><MatchAnalysis /></AppShell></ProtectedRoute>} />
          <Route path="/student/nutrition" element={<ProtectedRoute><AppShell><Nutrition /></AppShell></ProtectedRoute>} />
          <Route path="/nutrition" element={<ProtectedRoute><AppShell><Nutrition /></AppShell></ProtectedRoute>} />
          
          <Route path="/coach" element={<ProtectedRoute><AppShell><CoachDashboard /></AppShell></ProtectedRoute>} />
          <Route path="/coach/analytics" element={<ProtectedRoute><AppShell><CoachAnalytics /></AppShell></ProtectedRoute>} />
          {/* Phase 296 — Coach QR Attendance Scanner. No AppShell — full-screen white for outdoor sun-readability. */}
          <Route path="/coach/sessions/:sessionId/qr-scanner" element={<ProtectedRoute><RoleGate allow={['coach', 'employee', 'manager', 'academy_owner', 'super_admin']}><CoachQrScannerScreen /></RoleGate></ProtectedRoute>} />
          {/* Phase 296.d — Parent QR-Scan Receiver. Deep-link target when parents scan the coach's QR via native camera. Any authenticated user; page enforces its own parent/student UX. */}
          <Route path="/scan" element={<ProtectedRoute><ParentScanReceiver /></ProtectedRoute>} />
          {/* Phase 298 — Academy Owner Reputation Recovery Inbox. Lists 1-3★ feedback with Claude-drafted recovery emails. */}
          <Route path="/recovery-inbox" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><AppShell><RecoveryInbox /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 302 — Front-desk iPad Kiosk Mode. Staff role required. NO AppShell — full-screen kiosk UX. */}
          <Route path="/kiosk" element={<ProtectedRoute><RoleGate allow={['employee', 'coach', 'manager', 'academy_owner', 'super_admin']}><KioskMode /></RoleGate></ProtectedRoute>} />
          <Route path="/employee" element={<ProtectedRoute><AppShell><EmployeePortal /></AppShell></ProtectedRoute>} />
          <Route path="/employee-portal" element={<ProtectedRoute><AppShell><EmployeePortal /></AppShell></ProtectedRoute>} />
          <Route path="/employee/chat" element={<ProtectedRoute><AppShell><EmployeeChat /></AppShell></ProtectedRoute>} />
          <Route path="/employee-chat" element={<ProtectedRoute><AppShell><EmployeeChat /></AppShell></ProtectedRoute>} />
          <Route path="/academy-chat" element={<ProtectedRoute><AppShell><EmployeeChat /></AppShell></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><AppShell><EmployeeChat /></AppShell></ProtectedRoute>} />
          
          <Route path="/students" element={<ProtectedRoute><AppShell><Students /></AppShell></ProtectedRoute>} />
          <Route path="/hrms" element={<ProtectedRoute><AppShell><HRMS /></AppShell></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><AppShell><Events /></AppShell></ProtectedRoute>} />
          {/* Phase 87 — Coach-side check-in scanner */}
          <Route path="/events/:eventId/check-in" element={<ProtectedRoute><AppShell><EventCheckIn /></AppShell></ProtectedRoute>} />
          {/* Phase 89A — SocialBee (super_admin always; academy_owner gated to Legend tier in the page) */}
          <Route path="/socialbee" element={<ProtectedRoute><AppShell><SocialBee /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/socialbee" element={<ProtectedRoute><AppShell><SocialBee /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/tiers" element={<ProtectedRoute><AppShell><SuperAdminTiers /></AppShell></ProtectedRoute>} />
          {/* Phase 266c — Comms A/B funnel (super_admin only) */}
          <Route path="/super-admin/comms-ab" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><SuperAdminCommsAB /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute><AppShell><Finance /></AppShell></ProtectedRoute>} />
          {/* Phase 304 — Financial Engine: Auto-Pay receipts + AI receipt scanner dashboard */}
          <Route path="/finance/autopilot" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><AppShell><FinanceAutopilot /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 308 — Staff Timesheets owner-only dashboard */}
          <Route path="/staff/timesheets" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><AppShell><StaffTimesheets /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 312 — Coach Performance Scorecards owner-only dashboard */}
          <Route path="/staff/scorecards" element={<ProtectedRoute><RoleGate allow={['academy_owner']}><AppShell><CoachScorecards /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 138H.b — Academy owner fee-dunning control panel */}
          <Route path="/finance/dunning" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><AppShell><DunningControlPanel /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 195.e — Super Admin Onboarding Media & CRM Blast Engine */}
          <Route path="/super-admin/walkthrough-blast" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><SuperAdminWalkthroughBlast /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 327 — `/super-admin/crm-directory` is now redirected to /sales-ai (see redirects above).
              The SuperAdminCrmDirectory component is preserved on disk in case we ever surface it again. */}
          {/* Phase 89E — Outbound Webhook Dispatcher (Super Admin) */}
          <Route path="/super-admin/webhooks" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><SuperAdminWebhooks /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 327 — `/super-admin/web-analytics` is now redirected to /sales-ai (see redirects above). */}
          {/* Phase 220 — Super Admin in-app walkthrough tutorial library */}
          <Route path="/super-admin/walkthroughs" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><SuperAdminWalkthroughs /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/analytics/payments" element={<ProtectedRoute><AppShell><PaymentAnalytics /></AppShell></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><AppShell><SettingsPage /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute><AppShell><SuperAdmin /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/hrms" element={<ProtectedRoute><AppShell><AdminHRMS /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/finance" element={<ProtectedRoute><AppShell><AdminFinance /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/academies" element={<ProtectedRoute><AppShell><AdminAcademies /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/academies/:academyId/:kind/:personId" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><AdminPersonProfile /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/super-admin/trials" element={<ProtectedRoute><AppShell><AdminTrials /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/invoices" element={<ProtectedRoute><AppShell><AdminInvoices /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/portfolio" element={<ProtectedRoute><AppShell><AdminPortfolio /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/announce" element={<ProtectedRoute><AppShell><AdminAnnounce /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/autonomous" element={<ProtectedRoute><AppShell><AutonomousLedger /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/billing-health" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><BillingHealthDashboard /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/super-admin/adoption" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><AdminAdoption /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/admin/scouting/digest/preferences" element={<ProtectedRoute><AppShell><DigestPreferences /></AppShell></ProtectedRoute>} />
          <Route path="/super-admin/video-analysis" element={<ProtectedRoute><AppShell><VideoAnalysis /></AppShell></ProtectedRoute>} />
          <Route path="/crm" element={<ProtectedRoute><AppShell><CRM /></AppShell></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute><AppShell><Communications /></AppShell></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><AppShell><HelpCenter /></AppShell></ProtectedRoute>} />
          <Route path="/communications" element={<ProtectedRoute><AppShell><Communications /></AppShell></ProtectedRoute>} />
          <Route path="/academy-crm" element={<ProtectedRoute><AppShell><AcademyCRM /></AppShell></ProtectedRoute>} />
          <Route path="/academy/inbound-crm" element={<ProtectedRoute><AppShell><InboundCRM /></AppShell></ProtectedRoute>} />
          <Route path="/kpi-dashboard" element={<ProtectedRoute><AppShell><KPIDashboard /></AppShell></ProtectedRoute>} />
          <Route path="/kpi" element={<ProtectedRoute><AppShell><KPIDashboard /></AppShell></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><AppShell><Subscription /></AppShell></ProtectedRoute>} />
          <Route path="/announcements" element={<ProtectedRoute><AppShell><NotificationsInbox /></AppShell></ProtectedRoute>} />
          <Route path="/parent" element={<ProtectedRoute><AppShell><ParentPortal /></AppShell></ProtectedRoute>} />
          <Route path="/parent/profile" element={<ProtectedRoute><AppShell><ParentPortal /></AppShell></ProtectedRoute>} />
          <Route path="/training-sessions" element={<ProtectedRoute><AppShell><TrainingSessions /></AppShell></ProtectedRoute>} />
          <Route path="/training" element={<ProtectedRoute><AppShell><TrainingSessions /></AppShell></ProtectedRoute>} />
          <Route path="/drills" element={<ProtectedRoute><AppShell><DrillLibrary /></AppShell></ProtectedRoute>} />
          {/* Phase 641 — Session Plans */}
          <Route path="/session-plans" element={<ProtectedRoute><AppShell><SessionPlansListPage /></AppShell></ProtectedRoute>} />
          <Route path="/session-plans/new" element={<ProtectedRoute><AppShell><SessionPlanBuilderPage /></AppShell></ProtectedRoute>} />
          <Route path="/session-plans/:id" element={<ProtectedRoute><AppShell><SessionPlanBuilderPage /></AppShell></ProtectedRoute>} />
          {/* Phase 640 — Video Analysis */}
          <Route path="/video-analysis" element={<ProtectedRoute><AppShell><VideoAnalysisListPage /></AppShell></ProtectedRoute>} />
          <Route path="/video-analysis/new" element={<ProtectedRoute><AppShell><VideoAnalysisStudioPage /></AppShell></ProtectedRoute>} />
          <Route path="/video-analysis/:id" element={<ProtectedRoute><AppShell><VideoAnalysisStudioPage /></AppShell></ProtectedRoute>} />
          <Route path="/share/video/:token" element={<PublicVideoAnalysisPage />} />
          <Route path="/injuries" element={<ProtectedRoute><AppShell><InjuryTracker /></AppShell></ProtectedRoute>} />
          {/* Phase 605 — Legacy /super-admin/lead-intel alias preserved as a
              single-hop redirect to the new AI Agency Command Center. */}
          <Route path="/super-admin/lead-intel" element={<Navigate to="/super-admin/agency" replace />} />
          <Route path="/message-logs" element={<ProtectedRoute><AppShell><MessageLogs /></AppShell></ProtectedRoute>} />
          <Route path="/delivery-logs" element={<ProtectedRoute><AppShell><MessageLogs /></AppShell></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppShell><Profile /></AppShell></ProtectedRoute>} />
          <Route path="/security" element={<ProtectedRoute><AppShell><Profile /></AppShell></ProtectedRoute>} />

          {/* Phase 200 — ArenaX (public + player flows + venue admin + super admin) */}
          <Route path="/arenax" element={<ArenaxLanding />} />
          <Route path="/arenax/venues" element={<ArenaxVenues />} />
          {/* Phase 127 — public venue page now serves the request→accept→pay flow */}
          <Route path="/arenax/venues/:slug" element={<ArenaxVenuePublic />} />
          <Route path="/arenax/venues/legacy/:venueId" element={<ArenaxVenueDetail />} />
          <Route path="/arenax/auth/login" element={<ArenaxLogin />} />
          <Route path="/arenax/auth/register" element={<ArenaxRegister />} />
          <Route path="/arenax/auth/verify" element={<ArenaxVerify />} />
          <Route path="/arenax/auth/callback" element={<ArenaxAuthCallback />} />
          <Route path="/arenax/player" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxPlayerDashboard /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/player/onboarding" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxPlayerOnboarding /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/player/bookings" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxPlayerBookings /></RoleGate></ProtectedRoute>} />
          {/* Phase 2A — Public Games Engine */}
          <Route path="/arenax/games" element={<ArenaxGames />} />
          <Route path="/arenax/games/host" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxHostGame /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/games/:gameId" element={<ArenaxGameDetail />} />
          {/* Phase 2B — Squads (retention layer) */}
          <Route path="/arenax/squads" element={<ArenaxSquads />} />
          <Route path="/arenax/squads/create" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxCreateSquad /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/squads/:squadId" element={<ArenaxSquadDetail />} />
          {/* Phase 2H — Tournaments */}
          <Route path="/arenax/tournaments" element={<ArenaxTournaments />} />
          <Route path="/arenax/tournaments/new" element={<ProtectedRoute><RoleGate allow={['player']}><ArenaxTournamentCreate /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/tournaments/:tournamentId" element={<ArenaxTournamentDetail />} />
          {/* Phase 2J — Eternal League Pages */}
          <Route path="/arenax/seasons" element={<ArenaxSeasons />} />
          <Route path="/arenax/seasons/:seasonId" element={<ArenaxSeasonDetail />} />
          {/* Phase 2K PR 4 — City Rankings */}
          <Route path="/arenax/rankings" element={<ArenaxRankings />} />
          {/* Phase 2L PR 2 — Citywide Live Feed */}
          <Route path="/arenax/city" element={<ArenaxCityFeed />} />
          {/* Phase 2M PR 1 — Sports Stories (editorial magazine) */}
          <Route path="/arenax/stories" element={<ArenaxStories />} />
          {/* Phase 2L PR 5 — Spectator Room overlay */}
          <Route path="/arenax/matches/:matchId/spectate" element={<ArenaxSpectatorRoom />} />
          <Route path="/arenax/rivalries/:squadA/:squadB/documentary" element={<ArenaxRivalryDocumentary />} />
          <Route path="/arenax/join-squad/:code" element={<ArenaxJoinSquad />} />
          <Route path="/arenax/venue-admin" element={<ProtectedRoute><RoleGate allow={['academy_owner', 'super_admin']}><AppShell><ArenaxVenueAdmin /></AppShell></RoleGate></ProtectedRoute>} />
          <Route path="/super-admin/arenax" element={<ProtectedRoute><RoleGate allow={['super_admin']}><AppShell><ArenaxSuperAdmin /></AppShell></RoleGate></ProtectedRoute>} />
          {/* Phase 2R-Freeze — Realtime Ops dashboard (super_admin only, no AppShell — standalone diagnostic view) */}
          <Route path="/arenax/ops" element={<ProtectedRoute><RoleGate allow={['super_admin']}><ArenaxOps /></RoleGate></ProtectedRoute>} />
          {/* Phase 2R-MythologyOps — cultural observability layer (super_admin only) */}
          <Route path="/arenax/ops/mythology" element={<ProtectedRoute><RoleGate allow={['super_admin']}><ArenaxMythologyOps /></RoleGate></ProtectedRoute>} />
          <Route path="/arenax/host" element={<ArenaxOperatorHost />} />
          {/* Phase 124 — Operator Activation registration flows */}
          <Route path="/arenax/host/academy" element={<ArenaxHostAcademy />} />
          <Route path="/arenax/host/venue" element={<ArenaxHostVenue />} />
          <Route path="/arenax/host/organizer" element={<ArenaxHostOrganizer />} />
          {/* Phase 126 — Operator Manage Surfaces (Operational Sports Infrastructure) */}
          <Route path="/arenax/host/academy/manage" element={<ArenaxHostAcademyManage />} />
          <Route path="/arenax/host/venue/manage" element={<ArenaxHostVenueManage />} />
          <Route path="/arenax/host/organizer/manage" element={<ArenaxHostOrganizerManage />} />
          {/* Phase 127 — Civic Sports Operating Loop (player ledger) */}
          <Route path="/arenax/me/bookings" element={<ArenaxMyBookings />} />
          {/* Phase 178.5.F — dev sandbox for context menu primitive (no nav entry, no auth gate) */}
          <Route path="/_sandbox/context-menu" element={<ContextMenuSandbox />} />


          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" expand={true} richColors />
      </BrowserRouter>
      </AnalyticsProvider>
      </ArenaxCapabilityProvider>
      </PwaInstallProvider>
    </AuthProvider>
  );
}

export default App;
